// Daily reports (20261014180000 + 20261015163000): a person's own note of their
// day. The MCP agent may draft it; only the person sends it, from My space, to
// everyone above them in the reporting chain (dr_read: is_above). Sending goes
// through send_daily_report, which also tells their direct manager.
import { supabase } from './supabase';
import { write } from './write';
import { fmtDuration } from './duration';

export type DailyReport = {
  report_date: string;
  body: string;
  status: 'draft' | 'sent';
  sent_at: string | null;
  drafted_by: 'author' | 'agent';
  author_edited_at: string | null;
};
export type TeamReport = DailyReport & { profile_id: string; name: string | null };
type Failed = { failed?: true };

const COLS = 'report_date, body, status, sent_at, drafted_by, author_edited_at';
const failed = <T>(): T[] & Failed => Object.assign([] as T[], { failed: true as const });

/** This person's newest reports, today's included. */
export async function listMine(uid: string): Promise<DailyReport[] & Failed> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('daily_reports')
    .select(COLS)
    .eq('profile_id', uid)
    .order('report_date', { ascending: false })
    .limit(15);
  return error ? failed() : ((data as DailyReport[]) ?? []);
}

/**
 * Sent reports from people below this viewer. RLS returns only those: another
 * person's row is readable only once sent and only from above them.
 * ponytail: newest 300, filtered in the browser; filter server-side if a team outgrows it.
 */
export async function listTeam(uid: string): Promise<TeamReport[] & Failed> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('daily_reports')
    .select(`profile_id, ${COLS}, profiles(full_name)`)
    .neq('profile_id', uid)
    .eq('status', 'sent')
    .order('report_date', { ascending: false })
    .order('sent_at', { ascending: false })
    .limit(300);
  if (error) return failed();
  type Row = DailyReport & { profile_id: string; profiles: { full_name: string | null } | null };
  return ((data as unknown as Row[]) ?? []).map(({ profiles, ...r }) => ({
    ...r,
    name: profiles?.full_name ?? null,
  }));
}

/** Save the author's draft. Null on success, else what to tell them. */
export async function saveDraft(uid: string, date: string, body: string): Promise<string | null> {
  if (!supabase) return 'Not connected.';
  const r = await write(
    supabase
      .from('daily_reports')
      .upsert(
        { profile_id: uid, report_date: date, body },
        { onConflict: 'profile_id,report_date' },
      ),
    'report_date',
  );
  if (r.ok) return null;
  return r.reason === 'refused' ? 'That report was sent; a sent report is final.' : r.error.message;
}

/** Send the saved draft up the chain. Null on success, else the server's reason. */
export async function sendReport(date: string): Promise<string | null> {
  if (!supabase) return 'Not connected.';
  const { error } = await supabase.rpc('send_daily_report', { p_date: date });
  return error ? error.message : null;
}

// time_allocation (20261014190000): one row per project (and task) the day's
// timers ran on, plus a project_id null row for the rest and the check-out notes.
export type AllocRow = {
  project_id: string | null;
  seconds: number;
  worked_seconds: number;
  notes: string[];
};

/** A plain starting draft from the day's time: no AI, just what was logged. */
export const templateFrom = (rows: AllocRow[], names: Map<string, string>): string => {
  if (!rows.length) return '';
  const byProject = new Map<string, { seconds: number; notes: string[] }>();
  for (const r of rows) {
    if (!r.project_id || r.seconds <= 0) continue;
    const p = byProject.get(r.project_id) ?? { seconds: 0, notes: [] };
    p.seconds += r.seconds;
    p.notes.push(...r.notes);
    byProject.set(r.project_id, p);
  }
  const lines = [`Worked ${fmtDuration(rows[0].worked_seconds, 'hm')} today.`];
  if (byProject.size) {
    lines.push('');
    for (const [id, p] of byProject) {
      const notes = p.notes.length ? ` — ${p.notes.join('; ')}` : '';
      lines.push(`- ${names.get(id) ?? 'A project'}: ${fmtDuration(p.seconds, 'hm')}${notes}`);
    }
    const rest = rows.find((r) => !r.project_id);
    if (rest && rest.seconds > 0)
      lines.push(`- Not on a project: ${fmtDuration(rest.seconds, 'hm')}`);
  }
  const summary = rows.find((r) => !r.project_id)?.notes ?? [];
  if (summary.length) lines.push('', ...summary);
  return lines.join('\n');
};

/** Today's time as a starting draft. Empty when nothing is logged yet (or the read fails). */
export async function draftFromTime(uid: string, date: string): Promise<string> {
  if (!supabase) return '';
  const { data } = await supabase
    .from('time_allocation')
    .select('project_id, seconds, worked_seconds, notes')
    .eq('user_id', uid)
    .eq('work_date', date);
  const rows = (data as AllocRow[]) ?? [];
  const ids = [...new Set(rows.flatMap((r) => (r.project_id ? [r.project_id] : [])))];
  const names = new Map<string, string>();
  if (ids.length) {
    const { data: ps } = await supabase.from('projects').select('id, name').in('id', ids);
    for (const p of (ps as { id: string; name: string }[]) ?? []) names.set(p.id, p.name);
  }
  return templateFrom(rows, names);
}
